import { useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { glyphStyle } from "@/lib/glyphStyle";

const QUICK_EMOJIS = [
  "❤️", "💕", "🌹", "🍁", "🍂", "❄️", "⭐", "✨", "🎉", "🎈",
  "🌸", "🌻", "🌈", "☀️", "🍀", "🍓", "🍔", "🌮", "🍕", "🧁",
  "🎃", "👻", "🎄", "🎁", "🐰", "🥚", "🎆", "🦃", "🍬", "🎓",
];

const NEW_KEY = () => `custom_${Math.random().toString(36).slice(2, 10)}`;
const EMPTY = { key: "", name: "", primary: "#9b2226", accent: "#eab308", glyphs: [], glyph_color: "" };

const splitGlyphs = (text) =>
  Array.from(new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(text))
    .map((s) => s.segment.trim())
    .filter((g) => g && /\p{Extended_Pictographic}|\p{Regional_Indicator}/u.test(g))
    .slice(0, 5);

function ThemePreview({ draft }) {
  const glyphs = draft.glyphs.length ? draft.glyphs : [];
  return (
    <div
      data-testid="custom-theme-preview"
      className="relative overflow-hidden rounded-xl border border-stone-200 bg-stone-900 text-white"
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {glyphs.length > 0 && Array.from({ length: 8 }, (_, i) => (
          <span
            key={i}
            className="preview-fall"
            style={{
              left: `${(i * 13 + 5) % 100}%`,
              fontSize: `${12 + (i % 3) * 4}px`,
              animationDuration: `${6 + (i % 4)}s`,
              animationDelay: `-${(i * 1.7) % 6}s`,
              ...glyphStyle(draft.glyph_color),
            }}
          >
            {glyphs[i % glyphs.length]}
          </span>
        ))}
      </div>
      <div className="relative px-4 py-4 text-center space-y-1.5">
        <div className="font-display text-sm" style={{ color: draft.accent }}>Welcome to</div>
        <div className="font-display text-2xl leading-tight" style={{ color: draft.accent }}>Blossom's Kitchen</div>
        <div className="text-[10px] uppercase tracking-widest text-stone-300">We are not fast food. We are goooooood food!</div>
        <div
          className="inline-block mt-2 px-4 py-1.5 rounded-full text-xs font-bold text-white"
          style={{ background: draft.primary }}
        >
          Order Now
        </div>
      </div>
      <div className="relative flex items-center justify-between px-3 py-2 bg-white text-stone-900">
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: draft.primary }}>
          Open Now
        </span>
        <span className="text-[10px] font-bold" style={{ color: draft.primary }}>Rewards · Track</span>
        <span className="w-4 h-4 rounded-full" style={{ background: draft.accent }} />
      </div>
    </div>
  );
}

function ThemeForm({ initial, onSave, onCancel, busy }) {
  const [draft, setDraft] = useState({ ...EMPTY, ...initial });
  const glyphText = draft.glyphs.join("");
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const canSave = draft.name.trim().length > 0;

  return (
    <div className="rounded-xl border-2 border-brand-red/30 bg-stone-50 p-4 space-y-3" data-testid="custom-theme-form">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Live preview</div>
      <ThemePreview draft={draft} />
      <input
        value={draft.name}
        onChange={(e) => set("name", e.target.value.slice(0, 40))}
        placeholder="Theme name (e.g. Razorbacks Game Day)"
        data-testid="custom-theme-name-input"
        className="w-full px-3 py-2 rounded-lg border-2 border-stone-300 bg-white text-sm font-bold outline-none focus:border-brand-red"
      />
      <div className="grid grid-cols-2 gap-3">
        {[["primary", "Main color"], ["accent", "Accent color"]].map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 text-xs font-bold text-stone-600">
            <input
              type="color"
              value={draft[k]}
              onChange={(e) => set(k, e.target.value)}
              data-testid={`custom-theme-${k}-input`}
              className="w-9 h-9 rounded-md border border-stone-300 cursor-pointer bg-white"
            />
            <span>{label}<div className="font-mono text-[10px] text-stone-400">{draft[k]}</div></span>
          </label>
        ))}
      </div>
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
          Falling emojis (optional, up to 5)
        </div>
        <input
          value={glyphText}
          onChange={(e) => set("glyphs", splitGlyphs(e.target.value))}
          placeholder="Type or paste emojis, or tap below"
          data-testid="custom-theme-glyphs-input"
          className="w-full px-3 py-2 rounded-lg border-2 border-stone-300 bg-white text-lg outline-none focus:border-brand-red"
        />
        <div className="flex flex-wrap gap-1" data-testid="custom-theme-emoji-grid">
          {QUICK_EMOJIS.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => set("glyphs", splitGlyphs(glyphText + g))}
              disabled={draft.glyphs.length >= 5}
              className="w-8 h-8 rounded-md bg-white border border-stone-200 text-base hover:border-brand-red disabled:opacity-30"
            >
              {g}
            </button>
          ))}
        </div>
        {draft.glyphs.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="custom-theme-glyph-color-row">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Emoji color</span>
            <div className="inline-flex rounded-full border-2 border-stone-200 overflow-hidden text-xs font-bold">
              <button
                type="button"
                onClick={() => set("glyph_color", "")}
                data-testid="custom-theme-glyph-color-original"
                className={`px-3 py-1 ${!draft.glyph_color ? "bg-brand-red text-white" : "bg-white text-stone-600"}`}
              >
                Original
              </button>
              <button
                type="button"
                onClick={() => set("glyph_color", draft.glyph_color || draft.accent)}
                data-testid="custom-theme-glyph-color-tinted"
                className={`px-3 py-1 ${draft.glyph_color ? "bg-brand-red text-white" : "bg-white text-stone-600"}`}
              >
                One color
              </button>
            </div>
            {draft.glyph_color && (
              <label className="flex items-center gap-2">
                <input
                  type="color"
                  value={draft.glyph_color}
                  onChange={(e) => set("glyph_color", e.target.value)}
                  data-testid="custom-theme-glyph-color-input"
                  className="w-8 h-8 rounded-md border border-stone-300 cursor-pointer bg-white"
                />
                <span className="text-lg" style={glyphStyle(draft.glyph_color)}>{draft.glyphs.join(" ")}</span>
              </label>
            )}
          </div>
        )}
      </div>
      <div className="flex gap-2 pt-1">
        <button
          onClick={() => onSave({ ...draft, name: draft.name.trim(), key: draft.key || NEW_KEY() })}
          disabled={!canSave || busy}
          data-testid="custom-theme-save-btn"
          className="flex-1 py-2 rounded-full bg-brand-red text-white text-sm font-bold disabled:opacity-40"
        >
          Save theme
        </button>
        <button
          onClick={onCancel}
          disabled={busy}
          data-testid="custom-theme-cancel-btn"
          className="px-4 py-2 rounded-full border-2 border-stone-300 text-sm font-bold text-stone-600"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/** List + editor for admin-made storefront themes. `themes` is the saved
 *  array; `onChange(nextThemes)` persists it. */
export function CustomThemesEditor({ themes, onChange, busy, activeKey }) {
  const [editing, setEditing] = useState(null); // null | theme object (key "" = new)
  const [confirmKey, setConfirmKey] = useState(null);

  const save = async (t) => {
    const exists = themes.some((x) => x.key === t.key);
    const next = exists ? themes.map((x) => (x.key === t.key ? t : x)) : [...themes, t];
    await onChange(next, exists ? `"${t.name}" updated` : `"${t.name}" created`);
    setEditing(null);
  };

  const remove = async (t) => {
    if (confirmKey !== t.key) {
      setConfirmKey(t.key);
      setTimeout(() => setConfirmKey((k) => (k === t.key ? null : k)), 3000);
      return;
    }
    setConfirmKey(null);
    await onChange(themes.filter((x) => x.key !== t.key), `"${t.name}" deleted`, t.key === activeKey);
  };

  return (
    <div className="space-y-2 pt-3 border-t border-stone-200" data-testid="custom-themes-editor">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
          Your custom themes
        </div>
        {!editing && (
          <button
            onClick={() => setEditing(EMPTY)}
            disabled={busy || themes.length >= 20}
            data-testid="custom-theme-new-btn"
            className="inline-flex items-center gap-1 text-xs font-bold text-brand-red disabled:opacity-40"
          >
            <Plus size={14} /> New theme
          </button>
        )}
      </div>

      {themes.length === 0 && !editing && (
        <div className="text-xs text-stone-400" data-testid="custom-themes-empty">
          None yet — make one for game days, anniversaries, or local festivals.
        </div>
      )}

      {themes.map((t) => (
        editing?.key === t.key ? (
          <ThemeForm key={t.key} initial={t} onSave={save} onCancel={() => setEditing(null)} busy={busy} />
        ) : (
          <div
            key={t.key}
            data-testid={`custom-theme-row-${t.key}`}
            className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-3 py-2"
          >
            <div className="flex gap-1 shrink-0">
              <span className="w-5 h-5 rounded-full border border-stone-200" style={{ background: t.primary }} />
              <span className="w-5 h-5 rounded-full border border-stone-200" style={{ background: t.accent }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate">{t.name}</div>
              <div className="text-xs text-stone-400 truncate" style={glyphStyle(t.glyph_color)}>{t.glyphs?.length ? t.glyphs.join(" ") : "no falling emojis"}</div>
            </div>
            <button
              onClick={() => setEditing(t)}
              disabled={busy}
              aria-label="Edit theme"
              data-testid={`custom-theme-edit-${t.key}`}
              className="p-2 rounded-md text-stone-500 hover:text-brand-red disabled:opacity-40"
            >
              <Pencil size={16} />
            </button>
            <button
              onClick={() => remove(t)}
              disabled={busy}
              data-testid={`custom-theme-delete-${t.key}`}
              className={`p-2 rounded-md text-xs font-bold disabled:opacity-40 ${confirmKey === t.key ? "bg-red-600 text-white px-3" : "text-stone-500 hover:text-red-700"}`}
            >
              {confirmKey === t.key ? "Tap again" : <Trash2 size={16} />}
            </button>
          </div>
        )
      ))}

      {editing && !editing.key && (
        <ThemeForm initial={EMPTY} onSave={save} onCancel={() => setEditing(null)} busy={busy} />
      )}
    </div>
  );
}
